import React, { useState, useEffect } from 'react';

const TerminalSetup = ({ restaurant }) => {
  const [config, setConfig] = useState(null);
  const [role, setRole] = useState('master');
  const [masterIp, setMasterIp] = useState('');
  const [masterPort, setMasterPort] = useState('3737');
  const [displayName, setDisplayName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [localIp, setLocalIp] = useState('');
  const [allIps, setAllIps] = useState([]);
  const [connectedClients, setConnectedClients] = useState([]);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const desktop = window.menuronDesktop;

  useEffect(() => {
    desktop.terminal.getConfig().then(cfg => {
      setConfig(cfg);
      setRole(cfg.role || 'master');
      setMasterIp(cfg.master_ip || '');
      setMasterPort(String(cfg.master_port || 3737));
      setDisplayName(cfg.display_name || '');
    });
    desktop.terminal.getLocalIp().then(res => {
      if (res && typeof res === 'object') {
        setLocalIp(res.primary || '');
        setAllIps(res.all || []);
      } else {
        setLocalIp(res || '');
      }
    });
  }, []);

  useEffect(() => {
    if (!config) return;
    if (config.role === 'master') {
      const refresh = () => desktop.terminal.getConnectedClients().then(setConnectedClients);
      refresh();
      const t = setInterval(refresh, 3000);
      return () => clearInterval(t);
    }
  }, [config]);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    const result = await desktop.terminal.testConnection();
    setTestResult(result);
    setTesting(false);
  };

  const handleSave = async () => {
    setError('');
    if (role === 'client' && !masterIp.trim()) {
      setError('Master terminal IP is required for client mode.');
      return;
    }
    setSaving(true);
    try {
      const updated = await desktop.terminal.setRole(role, masterIp.trim() || null, masterPort ? Number(masterPort) : null, displayName.trim() || null);
      setConfig(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e.message || 'Failed to save terminal settings.');
    } finally {
      setSaving(false);
    }
  };

  if (!config) return <div style={styles.loading}>Loading terminal config...</div>;

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <h2 style={styles.title}>Terminal Settings</h2>
        <p style={styles.subtitle}>
          Configure this terminal's role on the local network. One terminal must be the Master, it holds the local database and syncs to the cloud. All other terminals are Clients, that connect to the Master over LAN.
        </p>

        {/* Terminal ID (read-only) */}
        <div style={styles.field}>
          <label style={styles.label}>Terminal ID</label>
          <div style={styles.readOnly}>{config.terminal_id}</div>
          <p style={styles.hint}>Unique identifier for this installation. Do not share.</p>
        </div>

        {/* Display name */}
        <div style={styles.field}>
          <label style={styles.label}>Display Name</label>
          <input
            style={styles.input}
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            placeholder="e.g. Counter 1, Kitchen Display, Manager Desk"
          />
        </div>

        {/* Role selector */}
        <div style={styles.field}>
          <label style={styles.label}>Terminal Role</label>
          <div style={styles.roleRow}>
            <RoleCard
              selected={role === 'master'}
              onClick={() => setRole('master')}
              title="Master"
              description="Runs the local database. All other terminals connect to this one. Keep it always on."
              icon="🖥️"
            />
            <RoleCard
              selected={role === 'client'}
              onClick={() => setRole('client')}
              title="Client"
              description="Connects to the Master terminal over LAN. Does not store data locally."
              icon="💻"
            />
          </div>
        </div>

        {/* Master IP — only shown for client role */}
        {role === 'client' && (
          <div style={styles.field}>
            <label style={styles.label}>Master Terminal IP Address</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                style={{ ...styles.input, flex: 1 }}
                value={masterIp}
                onChange={e => setMasterIp(e.target.value)}
                placeholder="e.g. 192.168.1.100"
              />
              <input
                style={{ ...styles.input, width: '90px' }}
                value={masterPort}
                onChange={e => setMasterPort(e.target.value)}
                placeholder="3737"
                type="number"
              />
            </div>
            <p style={styles.hint}>
              Find the Master IP by opening Terminal Settings on the master machine — it shows the IP under "Network Address".
            </p>
          </div>
        )}

        {/* Master mode: show this machine's local IP + connected clients */}
        {role === 'master' && config.role === 'master' && (
          <div style={styles.infoBox}>
            <strong>Clients should connect to this machine.</strong><br />
            Enter this IP on each client terminal:<br />
            <code style={{ fontSize: '1.1rem', fontWeight: '700', letterSpacing: '1px' }}>{localIp || 'detecting...'}</code>
            {allIps.length > 1 && (
              <div style={{ marginTop: '10px', fontSize: '0.8rem', opacity: 0.8 }}>
                <strong>All network addresses on this machine:</strong>
                {allIps.map(({ name, address }) => (
                  <div key={address} style={{ marginTop: '3px', fontFamily: 'monospace' }}>
                    {address} <span style={{ opacity: 0.6 }}>({name})</span>
                  </div>
                ))}
                <div style={{ marginTop: '6px', opacity: 0.7 }}>Use the IP that matches your restaurant's WiFi/Ethernet network.</div>
              </div>
            )}
            {connectedClients.length > 0 && (
              <div style={{ marginTop: '10px' }}>
                <strong>{connectedClients.length} client{connectedClients.length > 1 ? 's' : ''} connected:</strong>
                {connectedClients.map(c => (
                  <div key={c.terminalId} style={{ fontSize: '0.82rem', marginTop: '4px' }}>
                    📟 {c.ip} — connected {new Date(c.connectedAt).toLocaleTimeString()}
                  </div>
                ))}
              </div>
            )}
            {connectedClients.length === 0 && (
              <div style={{ marginTop: '8px', fontSize: '0.82rem', opacity: 0.7 }}>No clients connected yet.</div>
            )}
          </div>
        )}

        {/* Client mode: test connection button — show after saving (config.role=client) */}
        {role === 'client' && config.role === 'client' && masterIp.trim() && (
          <div style={{ marginBottom: '24px' }}>
            <button
              style={{ ...styles.saveBtn, background: '#6b7280', marginBottom: '8px' }}
              onClick={handleTestConnection}
              disabled={testing}
            >
              {testing ? 'Testing...' : 'Test Connection to Master'}
            </button>
            {testResult && (
              <div style={testResult.reachable ? styles.infoBox : styles.errorBox}>
                {testResult.reachable
                  ? `✓ Connected to master successfully`
                  : `✗ Cannot reach master: ${testResult.reason}`}
              </div>
            )}
          </div>
        )}

        {error && <div style={styles.errorBox}>{error}</div>}

        <button
          style={{ ...styles.saveBtn, opacity: saving ? 0.7 : 1 }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Terminal Settings'}
        </button>

        <div style={styles.statusRow}>
          <span style={styles.statusDot(config.role === 'master' ? '#22c55e' : '#f59e0b')} />
          <span style={styles.statusText}>
            Currently: <strong>{config.role === 'master' ? 'Master Terminal' : `Client → ${config.master_ip || 'not configured'}`}</strong>
          </span>
        </div>
      </div>
    </div>
  );
};

const RoleCard = ({ selected, onClick, title, description, icon }) => (
  <div
    onClick={onClick}
    style={{
      flex: 1,
      padding: '16px',
      border: `2px solid ${selected ? '#FF4444' : '#e5e7eb'}`,
      borderRadius: '10px',
      cursor: 'pointer',
      background: selected ? '#fff5f5' : '#fff',
      transition: 'all 0.15s',
    }}
  >
    <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
    <div style={{ fontWeight: '700', fontSize: '1rem', color: selected ? '#FF4444' : '#111', marginBottom: '4px' }}>{title}</div>
    <div style={{ fontSize: '0.82rem', color: '#666', lineHeight: '1.4' }}>{description}</div>
  </div>
);

const styles = {
  page: {
    padding: '32px 20px',
    maxWidth: '640px',
    margin: '0 auto',
  },
  card: {
    background: '#fff',
    borderRadius: '14px',
    padding: '32px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: '700',
    color: '#111',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '0.9rem',
    color: '#555',
    lineHeight: '1.5',
    marginBottom: '28px',
  },
  field: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontWeight: '600',
    fontSize: '0.9rem',
    color: '#333',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1.5px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '0.95rem',
    outline: 'none',
    boxSizing: 'border-box',
  },
  readOnly: {
    padding: '10px 14px',
    background: '#f9fafb',
    border: '1.5px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '0.85rem',
    color: '#666',
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  hint: {
    fontSize: '0.8rem',
    color: '#888',
    marginTop: '6px',
  },
  roleRow: {
    display: 'flex',
    gap: '12px',
  },
  infoBox: {
    background: '#f0fdf4',
    border: '1px solid #86efac',
    borderRadius: '8px',
    padding: '14px 16px',
    fontSize: '0.88rem',
    color: '#166534',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  errorBox: {
    background: '#fef2f2',
    border: '1px solid #fca5a5',
    borderRadius: '8px',
    padding: '12px 16px',
    fontSize: '0.88rem',
    color: '#b91c1c',
    marginBottom: '16px',
  },
  saveBtn: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #FF4444, #FF6B00)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
    marginBottom: '16px',
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusDot: (color) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
  }),
  statusText: {
    fontSize: '0.85rem',
    color: '#555',
  },
  loading: {
    padding: '40px',
    textAlign: 'center',
    color: '#888',
  },
};

export default TerminalSetup;
